import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@services/baseQuery';
import { setACCGProfitAndCostList } from '@store/slices/accounting-and-finance/accg-profit-and-cost-list.slice';
import {
  CreateProfitOrCostPayload,
  ProfitAndCostDataList,
  UpdateProfitOrCostPayload,
} from '../../modules/accounting-and-finance/interface/profit-and-cost/profit-and-cost-interface';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';

export const profitAndCostApi = createApi({
  reducerPath: 'profitAndCostApi',
  baseQuery: baseQuery,
  tagTypes: ['Profit', 'Department'],
  endpoints: (builder) => ({
    getProfitOrCost: builder.query({
      query: (query) => {
        return `profit-and-cost/${query}`;
      },
      providesTags: ['Profit', 'Department'],
      transformResponse: async (response: ProfitAndCostDataList) => {
        return response?.data;
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data: response } = await queryFulfilled;

          if (!response || !arg?.includes('property')) return;

          dispatch(setACCGProfitAndCostList(response));
        } catch (error) {
          console.error(error);
        }
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      forceRefetch() {
        return true;
      },
    }),
    createProfitOrCost: builder.mutation({
      query: (payload: CreateProfitOrCostPayload) => ({
        url: 'profit-and-cost',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const statusCode = data?.status;
        if (statusCode >= 200 && statusCode < 300) {
          dispatch(
            profitAndCostApi.util.invalidateTags(['Profit', 'Department']),
          );
        }
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    updateProfitOrCost: builder.mutation({
      query: (params: {
        departmentID: string;
        payload: UpdateProfitOrCostPayload;
      }) => ({
        url: `profit-and-cost/${params.departmentID}`,
        method: 'PUT',
        body: encryptPayload(params.payload),
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const statusCode = data?.status;
        if (statusCode >= 200 && statusCode < 300) {
          dispatch(
            profitAndCostApi.util.invalidateTags(['Profit', 'Department']),
          );
        }
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    deleteProfitOrCost: builder.mutation({
      query: (payload) => ({
        url: `profit-and-cost/${payload}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const statusCode = data?.status;
        if (statusCode >= 200 && statusCode < 300) {
          dispatch(
            profitAndCostApi.util.invalidateTags(['Profit', 'Department']),
          );
        }
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
    }),
  }),
});

export const {
  useGetProfitOrCostQuery,
  useLazyGetProfitOrCostQuery,
  useCreateProfitOrCostMutation,
  useUpdateProfitOrCostMutation,
  useDeleteProfitOrCostMutation,
} = profitAndCostApi;
